import React from 'react';
import clsx from 'clsx';
import { Dropdown } from 'react-bootstrap';
import { useFileExplorerContext } from '../../hooks/useFileExplorerContext';
import { PathNode } from './Context/FileExplorerContext';
import { DropdownCustomToggle } from '../common/Dropdown';
import { EllipsisIcon, FolderIcon } from './FileExplorerIcons';

interface BreadcrumbItemProps {
  children: React.ReactNode;
  isActive?: boolean;
  onClick?: () => void;
  className?: string;
}

const MAX_PATH_LENGTH = 3;

// Single button component for both active and inactive states to
// maintain consistent interaction patterns

function BreadcrumbItem({
  children, isActive = false, onClick, className,
}: BreadcrumbItemProps) {
  return (
    <li className={clsx('breadcrumb-item m-0', { active: isActive }, className)}>
      <button
        type="button"
        className={clsx('btn p-0 m-0 px-1 text-decoration-none border-0', {
          'btn-ghost text-muted': isActive,
          'btn-link': !isActive,
        })}
        onClick={isActive ? undefined : onClick}
        disabled={isActive}
        aria-current={isActive ? 'page' : undefined}
      >
        {children}
      </button>
    </li>
  );
}

interface BreadcrumbDropdownItemProps {
  children: React.ReactNode;
  onClick: () => void;
}

function BreadcrumbDropdownItem({ children, onClick }: BreadcrumbDropdownItemProps) {
  return (
    <Dropdown.Item onClick={onClick} className="fs-sm px-4">
      {children}
    </Dropdown.Item>
  );
}

interface BreadcrumbMiddleDropdownProps {
  items: PathNode[];
  onNavigateToPath: (index: number) => void;
  startIndex: number; // base index for the items
}

function BreadcrumbMiddleDropdown(
  { items, onNavigateToPath, startIndex }: BreadcrumbMiddleDropdownProps) {
  return (
    <li className="breadcrumb-item m-0">
      <Dropdown className="d-inline-block">
        <Dropdown.Toggle
          as={DropdownCustomToggle}
          className="file-explorer-dropdown"
          id="file-explorer-breadcrumb-dropdown-toggle"
        >
          <EllipsisIcon className="rotate-90" />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {items.map((pathNode, idx) => (
            <BreadcrumbDropdownItem
              key={pathNode.id}
              onClick={() => onNavigateToPath(startIndex + idx)}
            >
              <FolderIcon />
              {pathNode.name}
            </BreadcrumbDropdownItem>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </li>
  );
}

interface BreadcrumbPathProps {
  path: PathNode[];
  onNavigateToPath: (index: number) => void;
}

// Renders the dynamic path portion of the breadcrumb based on current folder navigation
// Separates path logic from root navigation to allow independent modification
function BreadcrumbPath({ path, onNavigateToPath }: BreadcrumbPathProps) {
  if (!path || path.length === 0) {
    return null;
  }
  const isALongPath = path.length > MAX_PATH_LENGTH;

  if (!isALongPath) {
    // If the route is short, show all items
    return (
      <>
        {path.map((pathNode, index) => (
          <BreadcrumbItem
            key={pathNode.id}
            isActive={index === path.length - 1}
            onClick={() => onNavigateToPath(index)}
          >
            {pathNode.name}
          </BreadcrumbItem>
        ))}
      </>
    );
  }

  // If the route is long:
  // Show first item, dropdown with intermediates, last MAX_PATH_LENGTH-1 items
  const firstItem = path[0];
  const lastItems = path.slice(-(MAX_PATH_LENGTH - 1));
  const middleItems = path.slice(1, path.length - (MAX_PATH_LENGTH - 1));

  return (
    <>
      {/* Primer item */}
      <BreadcrumbItem
        key={firstItem.id}
        isActive={false}
        onClick={() => onNavigateToPath(0)}
      >
        {firstItem.name}
      </BreadcrumbItem>
      {/* Dropdown with intermediates */}
      {middleItems.length > 0 && (
        <BreadcrumbMiddleDropdown
          items={middleItems}
          onNavigateToPath={onNavigateToPath}
          startIndex={1}
        />
      )}
      {/* Last items */}
      {lastItems.map((pathNode, index) => {
        // The real index in the original path
        const realIndex = path.length - lastItems.length + index;
        return (
          <BreadcrumbItem
            key={pathNode.id}
            isActive={realIndex === path.length - 1}
            onClick={() => onNavigateToPath(realIndex)}
          >
            {pathNode.name}
          </BreadcrumbItem>
        );
      })}
    </>
  );
}

interface BreadcrumbRootProps {
  onNavigateToRoot: () => void;
  title?: string;
  className?: string;
}

// Separate root component to distinguish between "home" navigation and path navigation
// Allows for different styling and behavior for the root level vs nested paths
function BreadcrumbRoot({ onNavigateToRoot, title = 'Lifeplan Docs', className }: BreadcrumbRootProps) {
  return (
    <li className={clsx('breadcrumb-item m-0', className)}>
      <button
        type="button"
        className="btn btn-link p-0 px-1 m-0 text-decoration-none border-0"
        onClick={onNavigateToRoot}
      >
        {title}
      </button>
    </li>
  );
}

interface FileExplorerBreadcrumbProps {
  className?: string;
  rootTitle?: string;
}

// Main breadcrumb component that orchestrates
// navigation state and renders the complete breadcrumb trail
export function FileExplorerBreadcrumb({ className, rootTitle }: FileExplorerBreadcrumbProps = {}) {
  const { activeFolder, setActiveFolder } = useFileExplorerContext();

  const hasPath = Boolean(activeFolder?.path?.length);

  const handleNavigateToRoot = () => {
    setActiveFolder(null);
  };

  // Navigation maintains folder structure integrity by setting only ID and path
  // Actual folder data will be fetched via React Query using useActiveFolderData hook
  const handleNavigateToPath = (targetIndex: number) => {
    if (!activeFolder?.path || targetIndex < 0 || targetIndex >= activeFolder.path.length) return;

    const targetPathNode = activeFolder.path[targetIndex];
    const newPath = activeFolder.path.slice(0, targetIndex + 1);

    // Set active folder with ID, path, and preserved metadata
    setActiveFolder({
      id: targetPathNode.id,
      path: newPath,
      metadata: activeFolder.metadata,
    });
  };

  return (
    <nav
      className={clsx('breadcrumb-nav', { visible: hasPath, invisible: !hasPath }, className)}
      aria-label="breadcrumb"
    >
      <ol className="breadcrumb mb-0">
        <BreadcrumbRoot
          onNavigateToRoot={handleNavigateToRoot}
          title={rootTitle}
        />
        <BreadcrumbPath
          path={activeFolder?.path || []}
          onNavigateToPath={handleNavigateToPath}
        />
      </ol>
    </nav>
  );
}
